import { type ReactNode, useEffect, useRef } from 'react';

import { Link, useParams, useSearchParams } from 'react-router';

import { useAnalytics } from '@boc/analyticsV3';
import type {
  ProductDetailsFragment,
  ProductVariantDetailsWihoutProductFragment,
} from '@boc/framework/saleor-graphql';

import { useCart as useCartClient } from '@boc/framework/checkout/cart/use-cart';
import { useCart } from '@boc/framework/checkout/cart/provider';

import Button from '~/local/components/button';
import toast from '~/toast';
import { absoluteify, urls } from '~/urls';

interface IAddToCartButtonProps {
  product: ProductDetailsFragment;
  variant: ProductVariantDetailsWihoutProductFragment;
  quantity?: number;
  children: ReactNode;
  disabled: boolean;
}

const AddToCart = ({ disabled, variant, children, quantity, product }: IAddToCartButtonProps) => {
  const params = useParams();
  const { addItem, isAddingItem } = useCartClient();
  const [searchParams] = useSearchParams();
  const tracker = useAnalytics();

  const isDisabled = disabled || isAddingItem || product.isAvailable === false;

  const { cart } = useCart();
  const lastAddedVariantId = useRef<string | null>(null);

  const handleAddToCart = async () => {
    try {
      await addItem(variant.id, quantity || 1);

      // Track successful add to cart
      tracker.trackAddToCart({
        variant: { ...variant, product },
        quantity: quantity || 1,
        itemUrl: (v) => absoluteify(urls.products.details(params, { slug: v.product.slug })),
        checkout: cart,
      });

      // Show success notification
      toast.success(
        <div className="flex gap-12">
          <div className="">Your item has been added to cart</div>
          <Link to={urls.checkout.cart(params)} className="underline">
            Go to cart
          </Link>
        </div>,
        {
          id: `add-to-cart-success-${variant.id}`,
        }
      );

      lastAddedVariantId.current = variant.id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to add product to cart';

      // Check if error is due to insufficient stock
      const isInsufficientStock = errorMessage.includes('INSUFFICIENT_STOCK');

      toast.error(
        isInsufficientStock
          ? 'Not enough stock available for this item'
          : 'Failed to add product to cart',
        {
          id: `add-to-cart-error-${variant.id}`,
        }
      );
    }
  };

  useEffect(() => {
    if (searchParams.get('added-to-cart') && lastAddedVariantId.current !== variant.id) {
      lastAddedVariantId.current = variant.id;

      toast.success(
        <div className="flex gap-12">
          <div className="">Your item has been added to cart</div>
          <Link to={urls.checkout.cart(params)} className="underline">
            Go to cart
          </Link>
        </div>,
        {
          id: `add-to-cart-success-${variant.id}`,
        }
      );

      tracker.trackAddToCart({
        variant: { ...variant, product },
        quantity: quantity || 1,
        itemUrl: (v) => absoluteify(urls.products.details(params, { slug: v.product.slug })),
        checkout: cart,
      });
    }
  }, [params, searchParams, variant, product, quantity, cart, tracker]);

  return (
    <Button
      className="float-right md:w-full"
      disabled={isDisabled}
      onClick={handleAddToCart}
    >
      {children}
    </Button>
  );
};

export default AddToCart;
